import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class PasswordService {

  private readonly qwertyRows = [
    "1234567890",
    "qwertyuiop",
    "asdfghjkl",
    "zxcvbnm",
    "0987654321",
    "poiuytrewq",
    "lkjhgfdsa",
    "mnbvcxz"
  ];

  /** Validator di campo: complessità password */
  passwordComplexityValidator(): ValidatorFn {
    const rules: Array<(value: string) => ValidationErrors | null> = [
      // Lunghezza minima 8 caratteri
      (v) => v.length < 8
        ? { minLength: 'Deve contenere almeno 8 caratteri' }
        : null,

      // Presente almeno una lettera maiuscola
      (v) => !/[A-Z]/.test(v)
        ? { missingUppercase: 'Deve contenere almeno una lettera maiuscola' }
        : null,

      // Presente almeno una lettera minuscola
      (v) => !/[a-z]/.test(v)
        ? { missingLowercase: 'Deve contenere almeno una lettera minuscola' }
        : null,

      // Presente almeno un numero
      (v) => !/[0-9]/.test(v)
        ? { missingNumber: 'Deve contenere almeno un numero' }
        : null,

      // Presente almeno un carattere speciale
      // I caratteri speciali consentiti sono: . , ! / - _ @ $ * | % & = ? ^ [ ] ; : < >
      (v) => !/[.,!\/\-_@$*\|%&=\?\^\[\];:<>]/.test(v)
        ? { missingSpecialChar: 'Deve contenere almeno un carattere speciale consentito' }
        : null,

      // Caratteri ammessi: lettere, numeri e i caratteri speciali consentiti
      // Qualsiasi altro carattere è considerato non valido
      (v) => /[^a-zA-Z0-9.,!\/\-_@$*\|%&=\?\^\[\];:<>]/.test(v)
        ? { invalidSpecialChar: 'Sono presenti caratteri speciali non consentiti' }
        : null,

      // Non sono permesse 3 lettere uguali consecutive
      (v) => /([a-zA-Z])\1\1/.test(v)
        ? { repeatedChar: 'Non sono permesse 3 lettere uguali consecutive' }
        : null,

      // Non sono ammesse lettere accentate
      (v) => /[À-ÿ]/.test(v)
        ? { containsAccentedChars: 'Non sono ammesse lettere accentate' }
        : null,

      // Controllo sequenze QWERTY di 4 caratteri
      (v) => this.hasQwertySequence(v)
        ? { qwertySequence: 'Contiene una sequenza QWERTY non permessa' }
        : null,
    ];

    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (!value) return null;

      const errors = rules.reduce((acc, check) => {
        const err = check(value);
        return err ? { ...acc, ...err } : acc;
      }, {} as ValidationErrors);

      return Object.keys(errors).length ? errors : null;
    };
  }

  /** Controlla sequenze QWERTY di 4 caratteri */
  hasQwertySequence(value: string): boolean {
    const lower = value.toLowerCase();
    for (const row of this.qwertyRows) {
      for (let i = 0; i <= row.length - 4; i++) {
        const seq = row.slice(i, i + 4); // ✅ sostituito substr con slice
        const revSeq = seq.split('').reverse().join('');
        if (lower.includes(seq) || lower.includes(revSeq)) {
          return true;
        }
      }
    }
    return false;
  }
}
